import React, { useEffect, useId, useLayoutEffect, useState } from 'react'
import cn from 'classnames'
import styles from 'components/panels/branch-pull-indicator.module.scss'
import { DRAW_EXTENT, resolveBranchPullHeight } from 'components/panels/branchPullGeometry'
import { PLACE_BAR_SELECTOR } from 'lib/fixedHeader'

// Pull-to-refresh draws a branch — trunk first, then two forks with node dots,
// then a bud at the tip once the pull is deep enough to refresh. While
// refreshing, light pulses run root→tips along the same paths. Geometry lives
// in a 320x100 viewBox anchored to the top edge. Nothing is covered: the column
// rides the pull (useFeedRidesPull translates the scroll container by this
// component's height), and the branch draws in the void that opens above the
// fold. Height math lives in branchPullGeometry.ts.

// Mirrors .branchPullIndicator's height transition so band and feed move on
// the same curve. Long and soft so release, refresh and retract glide like
// the tail of a scroll rather than snapping.
const FEED_RIDE_TRANSITION = 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)'
const FEED_RIDE_SETTLE_MS = 560

// The redesign's pull moves the column instead of covering it: translate the
// scroll container down by the void height while the branch draws above the
// fold. Inline styles rather than a class because the value tracks the hand at
// per-event granularity. Both pull components call this next to rendering the
// indicator; `enabled: false` (flag off / legacy) leaves the container
// untouched.
export function useFeedRidesPull(
    scrollContainerRef: React.RefObject<HTMLDivElement>,
    {
        enabled,
        isPulling,
        isRefreshing,
        pullProgress,
    }: { enabled: boolean; isPulling: boolean; isRefreshing: boolean; pullProgress: number },
) {
    const height = enabled ? resolveBranchPullHeight(isPulling, isRefreshing, pullProgress) : 0
    // Same transition regime as the indicator: 1:1 with the hand while
    // pulling, eased for release/refresh/retract.
    const tracking = isPulling && !isRefreshing

    useLayoutEffect(() => {
        const el = scrollContainerRef.current
        if (!el) return
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        // fixed_header: the panel's header sits beside the scroller (over the
        // top of it), so it rides the pull with the feed and the branch draws
        // above it.
        const bar = el.parentElement?.querySelector<HTMLElement>(PLACE_BAR_SELECTOR) || null
        const ride = (transition: string, transform: string) => {
            for (const node of bar ? [el, bar] : [el]) {
                node.style.transition = transition
                node.style.transform = transform
            }
        }

        if (height > 0) {
            ride(tracking || reduced ? 'none' : FEED_RIDE_TRANSITION, `translateY(${height}px)`)
            el.style.willChange = 'transform'
            return
        }

        if (!el.style.transform) return
        ride(reduced ? 'none' : FEED_RIDE_TRANSITION, 'translateY(0px)')
        // A live transform turns the scroll container into a containing block
        // for fixed/absolute descendants — drop it entirely once retracted.
        const timeout = window.setTimeout(() => {
            ride('', '')
            el.style.willChange = ''
        }, FEED_RIDE_SETTLE_MS)
        return () => window.clearTimeout(timeout)
    }, [scrollContainerRef, height, tracking])

    useEffect(() => {
        return () => {
            const el = scrollContainerRef.current
            if (el) {
                el.style.transform = ''
                el.style.transition = ''
                el.style.willChange = ''
                const bar = el.parentElement?.querySelector<HTMLElement>(PLACE_BAR_SELECTOR)
                if (bar) {
                    bar.style.transform = ''
                    bar.style.transition = ''
                }
            }
        }
    }, [scrollContainerRef])
}

// The panel's backdrop (desktop-bg) is not surface-base, so the void must stay
// painted while the feed slides home — unmounting the indicator the instant
// isRefreshing drops would flash the mismatched band under the retracting
// column. Both pull components keep their chrome mounted through this window.
export function usePullChromeLinger(active: boolean, ms = FEED_RIDE_SETTLE_MS): boolean {
    const [lingering, setLingering] = useState(false)

    useEffect(() => {
        if (active) {
            setLingering(true)
            return
        }
        if (!lingering) return
        const timeout = window.setTimeout(() => setLingering(false), ms)
        return () => window.clearTimeout(timeout)
    }, [active, ms, lingering])

    return !active && lingering
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))
// Maps overall progress to a segment's local 0→1 draw window, so limbs fork in
// sequence: trunk over 0–60%, left branch 35–78%, right branch 55–95%.
const seg = (progress: number, from: number, to: number) => clamp01((progress - from) / (to - from))

export interface BranchPullIndicatorProps {
    isPulling: boolean
    pullProgress: number
    isRefreshing: boolean
    showSpinner?: boolean
}

const BranchPullIndicator: React.FC<BranchPullIndicatorProps> = ({
    isPulling,
    pullProgress,
    isRefreshing,
    showSpinner = true,
}) => {
    // Several panels can mount their own indicator; the SVG gradient needs a
    // document-unique id or they all resolve to the first one.
    const gradientId = useId()

    // Height 0 during the linger window: the CSS height transition closes the
    // band on the same curve the feed rides home.
    const height = resolveBranchPullHeight(isPulling, isRefreshing, pullProgress)
    const progress = isRefreshing ? 1 : clamp01(height / DRAW_EXTENT)

    const trunkDrawn = seg(progress, 0, 0.6)
    const branchLeftDrawn = seg(progress, 0.35, 0.78)
    const branchRightDrawn = seg(progress, 0.55, 0.95)
    const armed = progress >= 1

    // All paths carry pathLength=100, so dasharray/dashoffset work in
    // normalized units regardless of real geometry length.
    const drawOffset = (drawn: number) => 100 * (1 - drawn)

    return (
        <div
            className={cn(
                styles.branchPullIndicator,
                isPulling && !isRefreshing && styles.pulling,
                isRefreshing && styles.refreshing,
            )}
            style={{ height: `${height}px` }}
            data-testid="pull-to-refresh-indicator"
        >
            {showSpinner && (
                <>
                    <svg
                        className={styles.branchSvg}
                        viewBox="0 0 320 100"
                        width="320"
                        height="100"
                        aria-hidden="true"
                    >
                        <defs>
                            {/* userSpaceOnUse: a plain vertical line has a zero-width
                                bounding box, and objectBoundingBox gradients render
                                nothing on it — the trunk would vanish. */}
                            <linearGradient
                                id={gradientId}
                                gradientUnits="userSpaceOnUse"
                                x1="120"
                                y1="0"
                                x2="210"
                                y2="90"
                            >
                                <stop offset="0" className={styles.gradStart} />
                                <stop offset="1" className={styles.gradEnd} />
                            </linearGradient>
                        </defs>
                        <g className={styles.limbs} stroke={`url(#${gradientId})`}>
                            <path
                                d="M160,0 L160,64"
                                pathLength={100}
                                style={{ strokeDashoffset: drawOffset(trunkDrawn) }}
                            />
                            <path
                                d="M160,24 Q150,37 130,42"
                                pathLength={100}
                                style={{ strokeDashoffset: drawOffset(branchLeftDrawn) }}
                            />
                            <path
                                d="M160,42 Q171,53 192,58"
                                pathLength={100}
                                style={{ strokeDashoffset: drawOffset(branchRightDrawn) }}
                            />
                        </g>
                        {isRefreshing && (
                            <g className={styles.pulses}>
                                <path d="M160,0 L160,64" pathLength={100} />
                                <path d="M160,24 Q150,37 130,42" pathLength={100} />
                                <path d="M160,42 Q171,53 192,58" pathLength={100} />
                            </g>
                        )}
                        <g fill={`url(#${gradientId})`}>
                            <circle
                                className={cn(styles.node, branchLeftDrawn >= 1 && styles.lit)}
                                cx="130"
                                cy="42"
                                r="4.5"
                            />
                            <circle
                                className={cn(styles.node, branchRightDrawn >= 1 && styles.lit)}
                                cx="192"
                                cy="58"
                                r="4.5"
                            />
                            <circle
                                className={cn(styles.node, styles.bud, armed && styles.lit)}
                                cx="160"
                                cy="64"
                                r="5.5"
                            />
                        </g>
                    </svg>
                </>
            )}
        </div>
    )
}

export default BranchPullIndicator
